import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { DialogContent, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { TravelMutationDto, TravelOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import FileCombobox, { FileComboboxProps } from '../../../../components/Combobox/FileCombobox'
import { useSettings } from '../../../../hooks/useSettings'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { IFileDto } from '../../../../config/fileConfig'
import { useFormikContext } from 'formik'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import useRoles, { rolesWriteAllTimes } from '../../../../hooks/useRoles'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import FormikDatePickerField from '../../../../components/Formik/FormikDatePickerField'
import { useFormikFieldStyles } from '../../../../components/Formik/FormikField'
import { IRefObject } from '@fluentui/utilities'
import { ITextField } from '@fluentui/react'
import useCurrency from '../../../../hooks/useCurrency'
import FormikMoneyField from '../../../../components/Formik/FormikMoneyField'
import FormikDistanceField from '../../../../components/Formik/FormikDistanceField'
import BillableCombobox, { BillableComboboxProps } from '../../../../components/Combobox/BillableCombobox'
import FormContainer from '../../Form/FormContainer'
import useDerivedFormValues from '../../../hooks/useDerivedFormValues'
import { multiplyRate } from '../../../../helpers/numberHelper'
import { resolveTravelRate } from '../../../../helpers/travelRateHelper'

const useStyles = makeStyles({
    billable: {
        flexGrow: 2,
    },
    rateRow: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: `2fr 3fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRowSmall: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
})

export const getValidationSchema = (translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        fileId: Yup.string().uuid().label(translate('registration.fileid')).required(),
        userId: Yup.string().uuid().label(translate('registration.userid')).required(),
        description: Yup.string().label(translate('registration.description')).required(),
        distance: Yup.string()
            .label(translate('travel.distance.label'))
            .required()
            .test('is-valid', translate('travel.distance.isvalid'), value => !isNaN(Number(value)) && Number(value) > 0),
        rate: Yup.string().label(translate('registration.rate')).required(),
        date: Yup.string().label(translate('registration.date')).required(),
        billable: Yup.string().label(translate('time.billable')).required(),
    })
}

export default function TravelMutationDialogBodyContent(
    props: HammockFormDialogProps<TravelMutationDto, BaseMutableOverviewDialogProps<TravelOverviewDto>> & {
        focusTrigger?: number
        updateFocusTrigger?: () => void
    },
): ReactElement {
    const axios = useAxios()
    const roles = useRoles()
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { initialValues, values } = useFormikContext<TravelMutationDto>()
    const { applyDerivedValues, onKeyDownCapture } = useDerivedFormValues<TravelMutationDto>()

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<TravelOverviewDto>), [props])

    const viewportSmall = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const isInvoiceDraft: boolean = dialogBodyProps.item?.invoice?.status === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft
    // Editing an existing line never re-suggests the stored rate (HAM-3627).
    const isEdit: boolean = !!dialogBodyProps.id

    const { currency } = useCurrency(values.currency ?? settings?.fileScaffold?.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])

    const [file, setFile] = useState<IFileDto>(undefined)

    const fileFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = useRef<ITextField>()
    const distanceFocusRef = useRef<HTMLInputElement>(null)

    // The rate value this dialog itself suggested; once values.rate deviates from it, the user
    // adjusted the rate manually and date changes must no longer overwrite it (HAM-3627).
    const autoRate = useRef<string>(undefined)

    useEffect(() => {
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!values.fileId) {
            fileFocusRef.current?.focus()
        } else if (!!initialValues.description || !values.description) {
            descriptionFocusRef.current?.focus()
        } else {
            distanceFocusRef.current?.focus()
        }
    }, [values.fileId, props.focusTrigger])

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(translate))
    }, [])

    useEffect(() => {
        if (values.fileId) {
            axios.get<IFileDto>(`/file/${values.fileId}`).then(response => setFile(response.data))
        }
    }, [values.fileId])

    useEffect(() => {
        if (isEdit || !file) {
            return
        }

        const resolved = resolveTravelRate(file.travelRate, settings?.travelRate, values.date)
        if (!values.rate || values.rate === autoRate.current) {
            applyDerivedValues({ rate: resolved, currency: file.currency })
            autoRate.current = resolved
        }
    }, [file, settings, values.date])

    useEffect(() => {
        applyDerivedValues({ rateTotal: multiplyRate(values.distance, { amount: values.rate, currency: values.currency }).amount })
    }, [values.rate, values.distance])

    const formWarning = dialogBodyProps.formWarning?.(values)

    return (
        <DialogContent onKeyDownCapture={onKeyDownCapture}>
            <FormContainer>
                {props.unsavedWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('base.unsaved.dialog')}</MessageBarBody>
                    </MessageBar>
                )}
                {formWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{formWarning}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceDraft && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('travel.edit.form.invoice.draft')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceFinal && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('travel.edit.form.invoice.final')}</MessageBarBody>
                    </MessageBar>
                )}

                <FormikComboboxField<TravelMutationDto, FileComboboxProps>
                    required
                    fieldName={'fileId'}
                    component={FileCombobox}
                    placeholderKey={'registration.fileid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || isInvoiceDraft,
                        disabled: isInvoiceFinal || isInvoiceDraft,
                        componentRef: fileFocusRef,
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <FormikTextareaField<TravelMutationDto>
                    fieldName="description"
                    placeholderKey={'registration.description'}
                    componentProps={{
                        required: true,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        ref: descriptionFocusRef,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikComboboxField<TravelMutationDto, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    placeholderKey={'registration.userid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        disabled: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <div className={styles.rateRow}>
                    <FormikDistanceField<TravelMutationDto>
                        required
                        fieldName={'distance'}
                        placeholderKey={'travel.distance.label'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            input: { ref: distanceFocusRef },
                        }}
                    />
                    <FormikMoneyField<TravelMutationDto>
                        required
                        fieldName={'rate'}
                        placeholderKey={'registration.rate'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            contentBefore: currencySymbol,
                        }}
                    />
                    <FormikMoneyField<TravelMutationDto>
                        required
                        fieldName={'rateTotal'}
                        placeholderKey={'registration.rate'}
                        componentProps={{
                            readOnly: true,
                            disabled: isInvoiceFinal,
                            tabIndex: -1,
                            appearance: 'filled-darker',
                        }}
                    />
                </div>
                <div className={mergeClasses(styles.dateRow, viewportSmall && styles.dateRowSmall)}>
                    <FormikDatePickerField<TravelMutationDto>
                        required
                        fieldName={'date'}
                        placeholderKey={'registration.date'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                        }}
                    />
                    <FormikComboboxField<TravelMutationDto, BillableComboboxProps>
                        required
                        fieldName={'billable'}
                        component={BillableCombobox}
                        placeholderKey={'time.billable'}
                        componentProps={{
                            placeholderPrefixKey: '',
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            onKeyDown: e => {
                                if (e.key === 'Tab') {
                                    e.preventDefault()
                                    props.updateFocusTrigger()
                                }
                            },
                        }}
                        className={!viewportSmall && styles.billable}
                    />
                </div>
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
